import { useQuery } from '@tanstack/react-query';
import { organizationSettingsApi } from '@/lib/api/organization-settings.api';
import type { BudgetOwnershipMode } from '@/lib/api/organization-settings.api';
import { queryKeys } from '../query-keys';
import { useOrganizationStore } from '@/store/organization.store';
import type { OrganizationSettings } from '@scrambl/shared';
import { getDefaultOrganizationSettings, getEffectiveSettings } from '@scrambl/shared';

interface OrganizationSettingsResponse {
  settings: OrganizationSettings;
  effectiveSettings: OrganizationSettings;
  budgetOwnershipMode: BudgetOwnershipMode;
  defaultMaxProjectCount: number | null;
}

/**
 * Hook to fetch organization settings.
 */
export function useOrganizationSettingsQuery(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore();

  return useQuery<OrganizationSettingsResponse>({
    queryKey: queryKeys.organizationSettings.detail(currentOrganization?.id || ''),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('Organization ID is required');
      }

      // Get organization settings
      let settings: OrganizationSettings = getDefaultOrganizationSettings();
      let budgetOwnershipMode: BudgetOwnershipMode = 'PROJECT';
      let defaultMaxProjectCount: number | null = null;
      try {
        const response = await organizationSettingsApi.getSettings(currentOrganization.id);
        settings = response.settings || settings;
        budgetOwnershipMode = response.budgetOwnershipMode ?? 'PROJECT';
        defaultMaxProjectCount = response.defaultMaxProjectCount ?? null;
      } catch (error) {
        const status = (error as { status?: number })?.status;
        if (status !== 403) {
          throw error;
        }
      }

      const effectiveSettings = getEffectiveSettings(settings);

      return {
        settings,
        effectiveSettings,
        budgetOwnershipMode,
        defaultMaxProjectCount,
      };
    },
    enabled: (options?.enabled !== false && !!currentOrganization?.id) ?? false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
